import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Receipt,
  Building2,
  FileText,
  Users,
  BarChart3,
  BellDot,
  PieChart,
  Megaphone,
  Gift,
  Store,
  CreditCard,
  Settings,
  Plug,
  Package,
  Ticket,
  ShieldCheck,
  Cloud,
  ToggleLeft,
  KeyRound,
  Mail,
  Bell,
  MessageSquare,
  Trash2,
  Server,
  Scale,
  Handshake,
  DollarSign,
  FileSpreadsheet,
  Video,
  Map,
  ScrollText,
  Webhook,
  TrendingUp,
  Calculator,
  Wallet,
  BadgePercent,
} from "lucide-react";

export type NavContext = "client" | "admin" | "super" | "partner";

export type NavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  /**
   * Se definido, item só visível para este e-mail específico.
   *
   * @deprecated Não use para decidir acesso. E-mail é ENDEREÇO, não
   * identificador: trocar o endereço da conta trancava o fundador para fora das
   * próprias ferramentas, e a Edge Function que de fato autoriza (`admin-reset`,
   * `create-comp-account`) sempre comparou UUID contra `PROTECTED_USER_UUID`.
   * Os dois itens que usavam este campo passaram a se apoiar no papel
   * `superadmin`, que `<SuperAdminRoutes>` já exige, com a recusa real ficando
   * no servidor. O campo continua declarado porque `SidebarNav` e
   * `CommandPalette` ainda o filtram; nenhum item o preenche hoje.
   */
  restrictToEmail?: string;
  /**
   * Bandeira (`feature_flags.flag_key`) que governa o item. Com a bandeira desligada — ou
   * ainda sem resposta — o item NÃO aparece: falha fechada. A rota, por sua vez, tem o
   * próprio portão (`FlagGate`); esconder o item não protege a rota.
   */
  flag?: string;
};

export type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

export type NavConfig = {
  context: NavContext;
  basePath: string;
  groups: NavGroup[];
  /** Itens exibidos no bottom-nav mobile (máx 5). */
  mobileItems?: NavItem[];
};

// ─── Contexto Cliente (/dashboard) ───────────────────────────────────────────

export const clientNav: NavConfig = {
  context: "client",
  basePath: "/",
  groups: [
    {
      id: "inicio",
      label: "INÍCIO",
      items: [
        { id: "dashboard", label: "Visão Geral", icon: LayoutDashboard, route: "/" },
      ],
    },
    {
      id: "imoveis",
      label: "GESTÃO DE IMÓVEIS",
      items: [
        { id: "meus-imoveis", label: "Meus Imóveis", icon: Building2, route: "/imoveis" },
        { id: "mapa-portfolio", label: "Mapa de Portfólio", icon: Map, route: "/mapa" },
        { id: "inquilinos", label: "Inquilinos", icon: Users, route: "/inquilinos" },
        { id: "contratos", label: "Contratos", icon: FileText, route: "/contratos" },
        { id: "integracoes", label: "Conectores", icon: Plug, route: "/conectores" },
      ],
    },
    {
      id: "financeiro",
      label: "FINANCEIRO",
      items: [
        { id: "painel-financeiro", label: "Painel Financeiro", icon: BarChart3, route: "/cobrancas" },
        { id: "alertas", label: "Alertas", icon: BellDot, route: "/alertas" },
        { id: "relatorios", label: "Relatórios", icon: PieChart, route: "/relatorios" },
        { id: "relatorio-ir", label: "IRPF", icon: FileSpreadsheet, route: "/relatorio-ir" },
        { id: "fiscal", label: "Carnê-leão", icon: Receipt, route: "/fiscal" },
        // Reforma tributária. Atrás da bandeira até os valores legais "a confirmar" serem validados.
        { id: "ibs-cbs", label: "IBS/CBS", icon: BadgePercent, route: "/fiscal/ibs-cbs", flag: "ibs_cbs_enrollment_enabled" },
        { id: "analytics", label: "Analytics BI", icon: TrendingUp, route: "/analytics" },
        // TODO: criar página /receitas — "Receitas e Despesas"
      ],
    },
    {
      id: "comunidade",
      label: "COMUNIDADE",
      items: [
        // "GIFI Anunciar" saiu daqui em 09/09/2026: dois módulos de classificados
        // no mesmo menu, com duas carteiras e regras de cobrança contrárias. Ver o
        // motivo em src/content/help/scope.ts.
        { id: "vitrine", label: "GIFI Vitrine", icon: Store, route: "/vitrine" },
        { id: "indicar", label: "Indicar", icon: Gift, route: "/indicar" },
        // TODO: criar versão cliente de Parceiros — /parceiros
      ],
    },
    {
      id: "conta",
      label: "CONTA",
      items: [
        { id: "planos", label: "Plano e Assinatura", icon: CreditCard, route: "/planos" },
        // `/assinatura` existia sem entrada de menu nenhuma: quem assinava não
        // tinha caminho para ver a renovação, trocar o cartão ou cancelar — só
        // chegava lá por link direto. É a tela de GERIR o que já se contratou,
        // separada da de ESCOLHER plano, e por isso são dois itens.
        { id: "assinatura", label: "Minha assinatura", icon: Wallet, route: "/assinatura" },
        { id: "tutoriais", label: "Tutoriais", icon: Video, route: "/tutoriais" },
        { id: "configuracoes", label: "Configurações", icon: Settings, route: "/configuracoes" },
        // TODO: criar página /suporte — "Suporte"
      ],
    },
  ],
  mobileItems: [
    { id: "dashboard-m", label: "Início", icon: LayoutDashboard, route: "/" },
    { id: "imoveis-m", label: "Imóveis", icon: Building2, route: "/imoveis" },
    { id: "contratos-m", label: "Contratos", icon: FileText, route: "/contratos" },
    { id: "financeiro-m", label: "Financeiro", icon: BarChart3, route: "/cobrancas" },
    { id: "alertas-m", label: "Alertas", icon: Bell, route: "/notificacoes" },
  ],
};

/**
 * Contexto Anunciante — quem vende na Vitrine.
 *
 * O painel Cliente tem 5 grupos e 19 itens, dos quais um anunciante usa DOIS.
 * Um encanador que entrasse por ali veria Meus Imóveis, Inquilinos, Contratos,
 * IRPF e Carnê-leão — nada disso é dele, e a tela ensina a duvidar se está no
 * lugar certo.
 *
 * Ninguém PERDE acesso por causa deste painel: quem tem imóveis continua com o
 * Cliente disponível no seletor, e quem cadastrar o primeiro imóvel amanhã passa
 * a ter os dois. O painel não tranca — ele para de oferecer o que não serve.
 *
 * `Plano e Assinatura` fica: é onde o anunciante vê a cota de anúncios sem custo
 * que o plano dele inclui. Tirar dali o faria procurar no lugar errado.
 */
export const partnerNav: NavConfig = {
  context: "partner",
  basePath: "/vitrine/meus-anuncios",
  groups: [
    {
      id: "anuncios",
      label: "MEUS ANÚNCIOS",
      items: [
        { id: "p-meus-anuncios", label: "Meus Anúncios", icon: Store, route: "/vitrine/meus-anuncios" },
        { id: "p-novo", label: "Novo Anúncio", icon: Megaphone, route: "/vitrine/novo" },
        { id: "p-leads", label: "Contatos", icon: Users, route: "/vitrine/leads" },
        { id: "p-creditos", label: "Créditos", icon: CreditCard, route: "/vitrine/creditos" },
      ],
    },
    {
      id: "vitrine",
      label: "VITRINE",
      items: [
        { id: "p-vitrine", label: "Ver a Vitrine", icon: Store, route: "/vitrine" },
        { id: "p-pedidos", label: "Pedidos", icon: FileText, route: "/parceiro/pedidos" },
      ],
    },
    {
      id: "conta",
      label: "CONTA",
      items: [
        { id: "p-planos", label: "Plano e Assinatura", icon: CreditCard, route: "/planos" },
        { id: "p-tutoriais", label: "Tutoriais", icon: Video, route: "/tutoriais" },
        { id: "p-configuracoes", label: "Configurações", icon: Settings, route: "/configuracoes" },
      ],
    },
  ],
  mobileItems: [
    { id: "p-anuncios-m", label: "Anúncios", icon: Store, route: "/vitrine/meus-anuncios" },
    { id: "p-novo-m", label: "Novo", icon: Megaphone, route: "/vitrine/novo" },
    { id: "p-leads-m", label: "Contatos", icon: Users, route: "/vitrine/leads" },
    { id: "p-creditos-m", label: "Créditos", icon: CreditCard, route: "/vitrine/creditos" },
    { id: "p-alertas-m", label: "Alertas", icon: Bell, route: "/notificacoes" },
  ],
};

/**
 * As rotas que o painel Anunciante alcança.
 *
 * Escrita a partir do menu acima, MAIS o que não tem item de menu e a pessoa
 * ainda precisa: o sino de notificações e a tela de assinatura. Uma lista
 * explícita, e não derivada só do menu, porque rota alcançável sem item de menu
 * é normal — e derivar automaticamente esconderia essa diferença.
 */
export const ROTAS_DO_ANUNCIANTE = [
  "/vitrine",
  "/vitrine/meus-anuncios",
  "/vitrine/novo",
  "/vitrine/creditos",
  "/vitrine/leads",
  "/parceiro/pedidos",
  "/planos",
  "/assinatura",
  "/tutoriais",
  "/configuracoes",
  "/notificacoes",
] as const;

// ─── Contexto Admin (/admin) ──────────────────────────────────────────────────

export const adminNav: NavConfig = {
  context: "admin",
  basePath: "/admin",
  groups: [
    {
      id: "visao-geral",
      label: "VISÃO GERAL",
      items: [
        { id: "overview", label: "Overview", icon: LayoutDashboard, route: "/admin" },
      ],
    },
    {
      id: "clientes",
      label: "CLIENTES",
      items: [
        { id: "clientes", label: "Clientes", icon: Users, route: "/admin/clientes" },
        { id: "assinaturas", label: "Assinaturas", icon: CreditCard, route: "/admin/assinaturas" },
        { id: "cupons", label: "Cupons", icon: Ticket, route: "/admin/cupons" },
      ],
    },
    {
      id: "produto",
      label: "PRODUTO",
      items: [
        { id: "planos", label: "Planos", icon: Package, route: "/admin/planos" },
        { id: "usuarios", label: "Usuários", icon: ShieldCheck, route: "/admin/usuarios" },
      ],
    },
    {
      id: "logs",
      label: "LOGS",
      items: [
        { id: "audit-logs", label: "Audit Logs", icon: ScrollText, route: "/admin/audit-logs" },
        { id: "webhook-logs", label: "Webhook Logs", icon: Webhook, route: "/admin/webhook-logs" },
      ],
    },
  ],
};

// ─── Contexto Super (/superadmin) ─────────────────────────────────────────────

export const superNav: NavConfig = {
  context: "super",
  basePath: "/superadmin",
  groups: [
    {
      id: "visao-geral",
      label: "VISÃO GERAL",
      items: [
        { id: "overview", label: "Visão Geral", icon: LayoutDashboard, route: "/superadmin" },
      ],
    },
    {
      id: "infraestrutura",
      label: "INFRAESTRUTURA",
      items: [
        { id: "servicos", label: "Serviços Cloud", icon: Cloud, route: "/superadmin/servicos" },
        { id: "infrastructure", label: "Infraestrutura", icon: Server, route: "/superadmin/infrastructure" },
        { id: "logs", label: "Logs do Sistema", icon: FileText, route: "/superadmin/logs" },
        // Sem `restrictToEmail`: quem autoriza o reset é `admin-reset`, pelo
        // UUID protegido. Esconder o item por endereço de e-mail dava a
        // impressão de controle e trancava o dono ao trocar de e-mail.
        { id: "reset", label: "Reset de Dados", icon: Trash2, route: "/superadmin/reset" },
        { id: "sefin", label: "Base SEFIN", icon: Building2, route: "/superadmin/condominios" },
        // TODO: confirmar posição de Custos — mapeado aqui por semântica de infra
        { id: "custos", label: "Custos", icon: DollarSign, route: "/superadmin/custos" },
        { id: "credenciais", label: "Credenciais", icon: KeyRound, route: "/superadmin/credenciais" },
      ],
    },
    {
      id: "desenvolvimento",
      label: "DESENVOLVIMENTO",
      items: [
        { id: "flags", label: "Feature Flags", icon: ToggleLeft, route: "/superadmin/feature-flags" },
        { id: "email-templates", label: "Templates de Email", icon: Mail, route: "/superadmin/email-templates" },
        { id: "notification-types", label: "Tipos de Notificação", icon: Bell, route: "/superadmin/notification-types" },
        { id: "messaging", label: "Mensageria", icon: MessageSquare, route: "/superadmin/messaging" },
      ],
    },
    {
      id: "gestao-interna",
      label: "GESTÃO INTERNA",
      items: [
        { id: "usuarios", label: "Usuários", icon: ShieldCheck, route: "/superadmin/users" },
        { id: "planos", label: "Planos", icon: Package, route: "/superadmin/plans" },
        { id: "fiscal", label: "Fiscal (IR)", icon: Calculator, route: "/superadmin/fiscal" },
        { id: "compliance", label: "Compliance", icon: Scale, route: "/superadmin/compliance" },
        // Mesma razão do Reset de Dados: `create-comp-account` decide pelo
        // `PROTECTED_USER_UUID`, não pelo endereço de quem está logado.
        { id: "comp-accounts", label: "Contas Comp", icon: Gift, route: "/superadmin/comp-accounts" },
      ],
    },
    {
      id: "features",
      label: "FEATURES",
      items: [
        // "GIFI Anunciar" saiu daqui em 09/09/2026, junto com o item do menu do
        // Cliente: o módulo foi aposentado e `/anunciar` redireciona para a
        // Vitrine. A tela de configuração e as tabelas ficam — o histórico é de
        // quem usou —, mas o menu não oferece mais a porta de um módulo que não
        // existe.
        { id: "vitrine-config", label: "GIFI Vitrine", icon: Store, route: "/superadmin/vitrine" },
        { id: "parceiros-config", label: "Parceiros", icon: Handshake, route: "/superadmin/partners" },
        { id: "tutoriais-config", label: "Tutoriais", icon: Video, route: "/superadmin/tutoriais" },
      ],
    },
  ],
};

/**
 * O item aparece para esta pessoa agora? Pura, para o menu e a paleta de comandos decidirem
 * pela MESMA regra: e-mail restrito (legado) e bandeira, esta última falhando fechada.
 */
export function navItemVisible(
  item: NavItem,
  ctx: { email?: string | null; flags: Record<string, boolean> },
): boolean {
  if (item.restrictToEmail && item.restrictToEmail !== ctx.email) return false;
  if (item.flag && ctx.flags[item.flag] !== true) return false;
  return true;
}

/** As bandeiras citadas por qualquer item do menu — o que o hook precisa consultar. */
export function navFlagKeys(config: NavConfig): string[] {
  return [...new Set(config.groups.flatMap((g) => g.items.flatMap((i) => (i.flag ? [i.flag] : []))))];
}
